import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Camera } from '@babylonjs/core/Cameras/camera';
import { Epsilon, Matrix, Vector2, Vector3 } from '@babylonjs/core/Maths';
import { Scene } from '@babylonjs/core/scene';
import { KbArcRotateCameraMouseWheelInput } from './kb-arc-rotate-camera-wheel-input';

/** Arc Rotate Camera (orbit camera) modified to offset the screen position on pan and keep the orbit target in the same position */
export class KbArcRotateCamera extends ArcRotateCamera {
    constructor(
        name: string,
        alpha: number,
        beta: number,
        radius: number,
        target: Vector3,
        private scene: Scene,
        setActiveOnSceneIfNoneActive?: boolean
    ) {
        super(name, alpha, beta, radius, target, scene, setActiveOnSceneIfNoneActive);

        //replace the wheel input with our own
        this.inputs.removeByType('ArcRotateCameraMouseWheelInput');
        this.inputs.add(new KbArcRotateCameraMouseWheelInput());
        this.updateClient();
    }
    inertialOffsetPanningX = 0;
    inertialOffsetPanningY = 0;
    inertialOrthoZoom = 0;
    clientInfo = {
        mouseX: 0,
        mouseY: 0,
        screenLeft: 0,
        screenTop: 0,
        screenWidth: 0,
        screenHeight: 0,
    };
    hasValidClientSize(): boolean {
        return this.clientInfo.screenWidth > 0 && this.clientInfo.screenHeight > 0;
    }
    lockRotation = false;
    lockPanning = false;
    lockZoom = false;
    limitMinimumTargetDistance = false;

    // When set, beta is forced to this exact value on every update() call.
    // Used by overhead cameras to prevent any per-frame drift from making
    // the camera tilt away from exactly vertical and revealing side faces.
    forcedBeta: number | null = null;

    private disposeListeners: (() => void) | undefined;
    private _orthoWidth = 0;
    get orthoWidth() {
        return this._orthoWidth;
    }
    set orthoWidth(val) {
        this._orthoWidth = this.clampOrthoWidth(val);
        this.updateOrthographicPosition();
    }

    orthoFit(sizeX: number, sizeY: number) {
        if (this.mode === Camera.ORTHOGRAPHIC_CAMERA && this.hasValidClientSize()) {
            const ratio = this.clientInfo.screenHeight / this.clientInfo.screenWidth;
            if (sizeX * ratio < sizeY) {
                this.orthoWidth = sizeY / ratio;
            } else {
                this.orthoWidth = sizeX;
            }
        }
    }

    update() {
        if (this.scene.activeCamera !== this) {
            return;
        }
        let startZoomLevel: number | undefined;
        if (!this.targetScreenOffset) {
            this.targetScreenOffset = new Vector2(0, 0);
        }

        if (this.lockRotation && (this.inertialAlphaOffset !== 0 || this.inertialBetaOffset !== 0)) {
            this.inertialPanningX = this.inertialAlphaOffset;
            this.inertialPanningY = -this.inertialBetaOffset;
            this.inertialAlphaOffset = this.inertialBetaOffset = 0;
        }

        if (this.inertialPanningX !== 0 || this.inertialPanningY !== 0) {
            if (!this.lockPanning) {
                this.inertialOffsetPanningX = this.inertialPanningX;
                this.inertialOffsetPanningY = this.inertialPanningY;
            }
            this.inertialPanningX = 0;
            this.inertialPanningY = 0;
        }

        if (this.lockZoom && this.inertialRadiusOffset !== 0) {
            this.inertialRadiusOffset = 0;
        }

        if (this.inertialRadiusOffset !== 0) {
            if (this.mode === Camera.ORTHOGRAPHIC_CAMERA) {
                this.inertialOrthoZoom = this.inertialRadiusOffset;
                this.inertialRadiusOffset = 0;
            } else {
                startZoomLevel = this.radius;
                //this.inertialRadiusOffset = 0;  //override so we have full control over the zoom
                //if (this.inertialRadiusOffset < .001) this.inertialRadiusOffset = .001;
            }
        }

        if (this.inertialOrthoZoom !== 0) {
            startZoomLevel = this.orthoWidth;
        }

        super.update();

        // Discard any rotation inertia generated inside super.update() by
        // inputs.checkInputs() (keyboard, mouse). We do this a second time because
        // checkInputs runs during super.update(), after our pre-processing above.
        // Without this, the inertia produced by the current frame's key press would
        // survive to the next frame and get converted to an unwanted pan.
        if (this.lockRotation) {
            this.inertialAlphaOffset = 0;
            this.inertialBetaOffset = 0;
        }

        if (this.inertialOffsetPanningX !== 0 || this.inertialOffsetPanningY !== 0) {
            let offset: Vector2;
            if (this.mode === Camera.ORTHOGRAPHIC_CAMERA) {
                offset = new Vector2(
                    this.inertialOffsetPanningX * this.orthoWidth,
                    this.inertialOffsetPanningY * this.orthoWidth
                );
            } else {
                offset = new Vector2(
                    this.inertialOffsetPanningX * this.radius,
                    this.inertialOffsetPanningY * this.radius
                );
            }

            if (this.forcedBeta !== null) {
                // For overhead cameras, move the actual target in world space instead of
                // adjusting targetScreenOffset. targetScreenOffset tilts the camera's look-at
                // direction away from vertical, making object sides visible when zoomed in.
                // With up=(0,0,-sign), LookAtLH gives xAxis=(-1,0,0): screen-right = world -X.
                // With up=(0,0,-sign), yAxis=(0,0,-sign): screen-up = world -Z (looking down).
                // Flip zSign and X sign relative to the original (up=(0,0,+sign)) code.
                const zSign = this.forcedBeta === Math.PI ? 1 : -1;
                this.target.x += offset.x;
                this.target.z += zSign * offset.y;
            } else {
                this.targetScreenOffset.subtractInPlace(offset);
            }

            this.inertialOffsetPanningX *= this.panningInertia;
            this.inertialOffsetPanningY *= this.panningInertia;
        }

        if (this.inertialOrthoZoom !== 0) {
            this.orthoWidth = this.orthoWidth - this.inertialOrthoZoom * this.orthoWidth;

            this.inertialOrthoZoom *= this.panningInertia;
        }

        if (Math.abs(this.inertialOffsetPanningX) < this.speed * Epsilon) {
            this.inertialOffsetPanningX = 0;
        }
        if (Math.abs(this.inertialOffsetPanningY) < this.speed * Epsilon) {
            this.inertialOffsetPanningY = 0;
        }
        if (Math.abs(this.inertialOrthoZoom) < this.speed * Epsilon) {
            this.inertialOrthoZoom = 0;
        }

        // When zooming, adjust the camera pan so you zoom into your mouse position, as long as pan is enabled
        if (!this.lockPanning && startZoomLevel !== undefined && this.hasValidClientSize()) {
            if (this.mode === Camera.ORTHOGRAPHIC_CAMERA) {
                const zoomChange = (startZoomLevel - this.orthoWidth) / 2;

                if (this.forcedBeta !== null) {
                    // Move target in world space to keep the camera perfectly vertical.
                    // Same forcedBeta !== null guard as in pan above: non-overhead cameras
                    // with lockRotation=true must use targetScreenOffset (original behavior).
                    // Orthographic height is scaled by the canvas ratio, so Z needs the same scale.
                    const verticalZoomChange =
                        zoomChange * (this.clientInfo.screenHeight / this.clientInfo.screenWidth);
                    const viewMatrix = this.getViewMatrix();
                    const screenRight = new Vector3(viewMatrix.m[0], viewMatrix.m[4], viewMatrix.m[8]);
                    const screenUp = new Vector3(viewMatrix.m[1], viewMatrix.m[5], viewMatrix.m[9]);

                    this.target.addInPlace(screenRight.scaleInPlace(zoomChange * this.clientInfo.mouseX));
                    this.target.addInPlace(screenUp.scaleInPlace(-verticalZoomChange * this.clientInfo.mouseY));
                } else {
                    this.targetScreenOffset.x -= zoomChange * this.clientInfo.mouseX;
                    this.targetScreenOffset.y += zoomChange * this.clientInfo.mouseY;
                }
            } else {
                const gamma = Math.tan(this.fov / 2);
                const zoomChange = startZoomLevel - this.radius;

                const shiftX = zoomChange * gamma * this.clientInfo.mouseX;
                const shiftY = zoomChange * gamma * this.clientInfo.mouseY;
                this.targetScreenOffset.x -= shiftX;
                this.targetScreenOffset.y += shiftY;
            }
        }
    }

    updateClient() {
        const canvas = this.getEngine().getRenderingCanvas();
        if (!canvas) {
            return;
        }

        const clientRect = canvas.getBoundingClientRect();
        this.clientInfo.screenWidth = clientRect.width;
        this.clientInfo.screenHeight = clientRect.height;
        this.clientInfo.screenLeft = clientRect.left;
        this.clientInfo.screenTop = clientRect.top;

        if (!this.hasValidClientSize()) {
            this.clientInfo.mouseX = 0;
            this.clientInfo.mouseY = 0;
            this.panningSensibility = 0;
            return canvas;
        }

        if (this.mode === Camera.ORTHOGRAPHIC_CAMERA) {
            this.updateOrthographicPosition();

            this.panningSensibility = clientRect.width;
        } else {
            const focalLength = clientRect.height / (2 * Math.tan(this.fov / 2));
            this.panningSensibility = focalLength;
        }

        return canvas;
    }

    updateMousePosition = (event: { clientX: number; clientY: number }) => {
        if (!this.hasValidClientSize()) {
            this.clientInfo.mouseX = 0;
            this.clientInfo.mouseY = 0;
            return;
        }

        this.clientInfo.mouseX =
            ((event.clientX - this.clientInfo.screenLeft) * 2) / this.clientInfo.screenWidth - 1;
        this.clientInfo.mouseY = ((event.clientY - this.clientInfo.screenTop) * 2) / this.clientInfo.screenHeight - 1;
    };

    attachControl(noPreventDefault = false) {
        super.attachControl(noPreventDefault, true);
        if (this.disposeListeners) {
            this.disposeListeners();
        }

        const canvas = this.updateClient();
        const updateClientWrap = () => this.updateClient();

        if (canvas) {
            window.addEventListener('resize', updateClientWrap);
            canvas.addEventListener('mousemove', this.updateMousePosition);

            this.disposeListeners = () => {
                window.removeEventListener('resize', updateClientWrap);
                canvas.removeEventListener('mousemove', this.updateMousePosition);
                this.disposeListeners = undefined;
            };
        }
    }

    // Forces the camera into an exact overhead orientation, looking either straight
    // down (positionY >= 0, beta = 0) or straight up (positionY < 0, beta = PI).
    // Two structural corrections are applied together:
    //   1. Beta limits - allows reaching exactly 0 and PI without clamping.
    //   2. Position sync - writes the exact overhead world position into _position
    //                    immediately. Without this, _position still holds the previous
    //                    cached value and any subscriber that reads camera.position before
    //                    the first render frame will compute the wrong direction, then
    //                    call setPosition with that direction, resetting beta via
    //                    rebuildAnglesAndRadius before the camera ever renders.
    // Additionally: forcedBeta locks beta on every _checkLimits call (called from
    // update() and rebuildAnglesAndRadius), and _getViewMatrix is overridden to
    // bypass the BabylonJS sinb=0 guard that would otherwise introduce a tiny
    // positional offset visible as side faces at extreme orthographic zoom.
    applySideCorrection(positionY: number) {
        // Remove beta clamping so the camera can reach exactly 0 and PI.
        this.lowerBetaLimit = 0;
        this.upperBetaLimit = Math.PI;

        // Set Z as the up vector so the input system (pointer, keyboard) maps mouse
        // drag-up to world +Z and drag-right to world +X. This keeps the pan direction
        // consistent with what _getViewMatrix renders. Without this, the input system
        // uses the default Y-up frame while the visual output uses Z-up, causing
        // the pan to feel inverted or rotated.
        this.upVector = new Vector3(0, 0, 1);

        // When the camera is above the target it looks straight down (beta = 0).
        // When the camera is below the target it looks straight up (beta = PI).
        const lookingDown = positionY >= 0;
        this.alpha = 0;
        this.beta = lookingDown ? 0 : Math.PI;

        // Lock beta on every frame so floating-point drift can never make side
        // faces visible when the user zooms in far in orthographic mode.
        this.forcedBeta = this.beta;

        const overheadRadius = this.mode === Camera.ORTHOGRAPHIC_CAMERA
            ? 5000
            : Math.max(Math.abs(positionY), 0.0001);
        this.radius = overheadRadius;

        // When orthographic, the camera is placed 5000 units from the target.
        // The bbCache subscription in applyViewpointProperties may have already
        // run synchronously and set maxZ to a small value based on mesh size.
        // That small maxZ would clip the mesh entirely since it is 5000 units away.
        // Reset maxZ here to guarantee it always covers the full overhead radius.
        if (this.mode === Camera.ORTHOGRAPHIC_CAMERA) {
            this.maxZ = 200000;
        }

        // Reset any accumulated targetScreenOffset. For overhead cameras, pan is
        // handled by moving this.target directly (see update()), so any leftover
        // offset here would tilt the camera away from vertical.
        this.targetScreenOffset.set(0, 0);

        // Sync _position immediately so subscribers that run before the
        // first render frame read the correct straight-vertical direction.
        const yOffset = lookingDown ? overheadRadius : -overheadRadius;
        this.setPosition(this.target.add(new Vector3(0, yOffset, 0)));
    }

    // Overrides BabylonJS _getViewMatrix to bypass the sinb===0 guard that exists
    // in the base class. That guard replaces sinb with 0.0001 when beta is exactly 0,
    // which offsets the camera slightly from vertical. Even a 0.0001-radian tilt is
    // visible when zoomed far in with an orthographic camera and reveals side faces.
    // When forcedBeta is set we place the camera directly above or below the target
    // using a hand-built LookAt instead of the spherical coordinate formula.
    _getViewMatrix(): Matrix {
        if (this.forcedBeta !== null) {
            const target = this._getTargetPosition();
            const sign = this.forcedBeta === 0 ? 1 : -1;

            // Place camera exactly above (sign=1) or below (sign=-1) target.
            // This bypasses the sinb===0 guard in the base class _getViewMatrix.
            this._newPosition.copyFromFloats(
                target.x,
                target.y + sign * this.radius,
                target.z
            );
            this._position.copyFrom(this._newPosition);

            // Use (0, 0, -sign) as the up vector to match the old camera rotation convention
            // so that rendered images (PNG exports) appear right-side up.
            // - sign=+1 (looking down): up=(0,0,-1) -- non-degenerate, forward=(0,-1,0).
            // - sign=-1 (looking up):   up=(0,0,+1) -- non-degenerate, forward=(0,+1,0).
            // targetScreenOffset is intentionally NOT applied here: any non-zero offset
            // would angle the camera away from vertical and reveal side faces at zoom.
            const up = new Vector3(0, 0, -sign);
            this._computeViewMatrix(this._position, target, up);

            this._currentTarget = target;
            return this._viewMatrix;
        }

        return super._getViewMatrix();
    }

    _checkLimits() {
        super._checkLimits();
        // forcedBeta is enforced here so that rebuildAnglesAndRadius (called whenever
        // setPosition is used externally) cannot drift beta away from 0 or PI.
        // _getViewMatrix bypasses beta entirely for overhead cameras, but keeping
        // forcedBeta in sync ensures position-reading code (e.g. bbCache subscriber)
        // gets a consistent camera.position value between frames.
        if (this.forcedBeta !== null) {
            this.beta = this.forcedBeta;
        }
        if (this.mode === Camera.ORTHOGRAPHIC_CAMERA) {
            this.orthoWidth = this.clampOrthoWidth(this.orthoWidth);
        }
    }

    private clampOrthoWidth(val: number) {
        if (this.mode === Camera.ORTHOGRAPHIC_CAMERA) {
            if (this.lowerRadiusLimit != null && val < this.lowerRadiusLimit) {
                val = this.lowerRadiusLimit;
            }
            if (this.upperRadiusLimit != null && val > this.upperRadiusLimit) {
                val = this.upperRadiusLimit;
            }
        }

        return val;
    }

    dispose() {
        super.dispose();
        if (this.disposeListeners) {
            this.disposeListeners();
        }
    }

    private updateOrthographicPosition() {
        if (!this.hasValidClientSize()) {
            return;
        }

        const ratio = this.clientInfo.screenHeight / this.clientInfo.screenWidth;
        this.orthoRight = this.orthoWidth / 2;
        this.orthoLeft = -this.orthoWidth / 2;
        this.orthoTop = (this.orthoWidth * ratio) / 2;
        this.orthoBottom = (-this.orthoWidth * ratio) / 2;
    }
}
